import { useFormik } from 'formik';
import { isEqual } from 'lodash';
import React, { useCallback } from 'react';
import { FaEuroSign } from 'react-icons/fa6';
import { IoClose } from 'react-icons/io5';
import { ActionIcon, Button, Checkbox, Modal, Text, cn } from 'rizzui';
import { toast } from 'sonner';
import * as yup from 'yup';
import CustomSelect from '../../../components/forms/CustomSelect';
import CustomSelectApiHookForm from '../../../components/forms/CustomSelectApiHookForms';
import DateField from '../../../components/forms/DateField';
import FormLabel from '../../../components/forms/FormLabel';
import RequiredInput from '../../../components/forms/InputHOC';
import useCorporations from '../../../hooks/api-calls/useCorporations';
import useFetch from '../../../hooks/useFetch';
import { ExpirationService } from '../../../services/expiration/expirationService';

interface RemittanceModalProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    refetch: () => void;
}

interface ExpirationData {
    amount: string,
    contractCode: string,
    description: string,
    expirationDate: string,
    id: string,
    invoiceNumber: string,
}

const schema = yup.object({
    expirationIds: yup.array().of(yup.string()).required('Este campo es obligatorio'),
    corporationId: yup.string().required('Este campo es obligatorio'),
    bankName: yup.string().required('Este campo es obligatorio'),
});

const RemittanceModal: React.FC<RemittanceModalProps> = ({ isOpen, setIsOpen, refetch }) => {

    // HOOKS

    const { fetchCorporations } = useCorporations({ active: true });

    // STATES

    const [isLoading, setIsLoading] = React.useState(false);
    const [remittanceData, setRemittanceData] = React.useState({
        dateRange: {
            from: new Date(new Date().getFullYear(), 0, 1).toISOString(),
            to: new Date().toISOString(),
        },
        corporationId: null as string | null,
        bankName: null as string | null,
        amount: null as number | null,
    });
    const [currentPage, setCurrentPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(20);

    const [banks] = useFetch(useCallback(async () => {
        if (!remittanceData.corporationId) return null;
        const response = await (new ExpirationService).listBanks({ corporationId: remittanceData.corporationId });
        return response.getResponseData();
    }, [remittanceData.corporationId]));

    const [expirations] = useFetch(useCallback(async () => {
        if (!remittanceData.dateRange.from || !remittanceData.dateRange.to || !remittanceData.corporationId || !remittanceData.bankName || !remittanceData.amount) return null;
        const response = await (new ExpirationService).obtainExpirationsToGenerateSEPA(remittanceData);
        return response.getResponseData();
    }, [remittanceData.dateRange, remittanceData.corporationId, remittanceData.bankName, remittanceData.amount]));

    // EXTRACTION OF EXPIRATIONS FOR PAGINATION
    let paginatedExpirations = expirations && expirations?.expirations_data
        ? expirations.expirations_data.slice((currentPage - 1) * pageSize, currentPage * pageSize)
        : [];
    const totalPages = expirations && expirations.expiration_ids
        ? Math.ceil(expirations.expiration_ids.length / pageSize)
        : 1;

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES ENVÍA LA SOLICITUD DE ASIGNACIÓN DE CONTRATO
     * @EN SENDS THE CONTRACT ASSIGNMENT REQUEST
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const submit = async (values: any) => {
        setIsLoading(true);
        try {
            const response = await (new ExpirationService).generateSEPA(values);

            if (response) {
                const xmlContent = response.getResponseData();

                if(xmlContent?.success === false)
                {
                    toast.error(xmlContent?.message);
                    return;
                }
                
                const blob = new Blob([xmlContent], { type: "application/xml" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `sepa_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}_${new Date().toTimeString().slice(0, 8).replace(/:/g, '')}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                toast.success('Remesa generada correctamente');
                onClose();
            } else {
                toast.error('Error al exportar el documento de la remesa');
            }
        } catch (error: any) {
            toast.error('Error al generar la remesa');
        } finally {
            setIsLoading(false);
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CIERRE DEL MODAL
     * @EN CLOSE THE MODAL
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const onClose = () => {
        refetch(); // Refetch data to update the list after generating the remittance
        formik.resetForm();
        setRemittanceData({
            dateRange: {
                from: new Date(new Date().getFullYear(), 0, 1).toISOString(),
                to: new Date().toISOString(),
            },
            corporationId: null,
            bankName: null,
            amount: null,
        });
        setCurrentPage(1);
        setIsOpen(false);
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    // FORMIK

    const formik = useFormik({
        initialValues: {
            expirationIds: [],
            corporationId: '',
            bankName: '',
        },
        validationSchema: schema,
        validateOnBlur: false,
        onSubmit: submit,
    });

    // RENDER

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size='xl'
            overlayClassName='backdrop-blur'
            containerClassName='!shadow-xl'
            className='z-[999] [&_.pointer-events-none]:overflow-visible'
        >
            <div className='m-auto px-7 pt-6 pb-4' >
                <div className='mb-7 flex items-center justify-between' >
                    <Text className="text-xl" > Generar SEPA </Text>
                    < ActionIcon size='sm' variant='text' onClick={onClose}>
                        <IoClose className='h-auto w-6' strokeWidth={1.8} />
                    </ActionIcon>
                </div>

                <form onSubmit={formik.handleSubmit} className='grid md:grid-cols-2 gap-3' >
                    <DateField
                        isClearable
                        className="w-full"
                        label={< span > Rango de fechas < span className="text-red-500" > * </span></span >}
                        placeholderText="Selecciona las fechas"
                        selectedDateField={"dateRange"}
                        startDate={new Date(remittanceData.dateRange.from as string) || null}
                        endDate={new Date(remittanceData.dateRange.to as string) || null}
                        onChange={(date: any) => {
                            const newRange = date ? { from: date[0], to: date[1] } : null;
                            if (newRange && !isEqual(remittanceData.dateRange, newRange)) {
                                setRemittanceData(prev => ({ ...prev, dateRange: { from: date[0], to: date[1] } }));
                            } else if (!newRange && remittanceData.dateRange) {
                                setRemittanceData((prev: any) => ({ ...prev, dateRange: { from: null, to: null } }));
                            }
                        }}
                    />

                    <CustomSelectApiHookForm
                        id="corporationId"
                        label="Empresa fiscal"
                        formik={formik}
                        fetchOptions={async (searchText: string | null) => {
                            return fetchCorporations({ search_text: searchText }) as any;
                        }}
                        error={formik.errors.corporationId}
                        minInputLength={2}
                        required
                        setValue={(value: any) => { setRemittanceData(prev => ({ ...prev, corporationId: value?.id })); }}
                    />

                    <CustomSelect
                        isSearchable
                        isClearable
                        id={'bankName'}
                        label="Banco"
                        value={banks?.find((g: any) => g.value === formik.values.bankName)}
                        options={banks?.map((bank: any) => ({
                            label: `${bank.name} - ${bank.iban}`,
                            value: bank.name,
                        }))}
                        onChange={(e: any) => {
                            formik.setFieldValue('bankName', e?.value);
                            setRemittanceData(prev => ({ ...prev, bankName: e?.value }));
                        }}
                        required
                        error={formik.errors.bankName}
                        isDisabled={!formik.values.corporationId || !banks?.length}
                    />

                    <RequiredInput
                        id='amount'
                        type="number"
                        label="Cantidad a remesar"
                        formik={formik}
                        onChange={(e: any) => {
                            const value = e.target.value ? parseFloat(e.target.value) : null;
                            formik.setFieldValue('amount', value);
                            setRemittanceData(prev => ({ ...prev, amount: value }));
                        }}
                        suffix={< FaEuroSign />}
                    />

                    {expirations && expirations.expiration_ids?.length > 0 && remittanceData.corporationId && remittanceData.bankName && remittanceData.amount && (
                        <div className='md:col-span-2'>
                            <div className='flex justify-between'>
                                <FormLabel label={`Vencimientos disponibles (${expirations.total_expirations_quantity})`} />
                                <Checkbox
                                    id="selectAll"
                                    label="Seleccionar todos"
                                    checked={formik.values.expirationIds.length === expirations.expiration_ids.length}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        formik.setFieldValue('expirationIds', checked ? expirations.expiration_ids : []);
                                    }}
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 overflow-y-auto" style={{ maxHeight: 300 }}>
                                {paginatedExpirations.map((exp: any) => (
                                    <label key={exp.id} className="flex items-center bg-white rounded-lg shadow p-4 border border-gray-200 hover:border-primary-500 transition cursor-pointer">
                                        <Checkbox
                                            id={exp.id}
                                            value={exp.id}
                                            checked={formik.values.expirationIds.find(id => id === exp.id) !== undefined}
                                            onChange={(e) => {
                                                const checked = e.target.checked;
                                                const newExpirationIds = checked
                                                    ? [...formik.values.expirationIds, exp.id]
                                                    : formik.values.expirationIds.filter(id => id !== exp.id);
                                                formik.setFieldValue('expirationIds', newExpirationIds);
                                            }}
                                            className="me-4"
                                        />
                                        <div className="flex-1">
                                            <div className="font-semibold text-base">Factura: {exp.invoiceNumber}</div>
                                            <div className="text-sm text-gray-600">Contrato: {exp.contractCode}</div>
                                            {exp.description && exp.description.trim() !== '' && (
                                                <div className="text-sm text-gray-500 mt-1">{exp.description}</div>
                                            )}
                                            <div className='mt-1'>
                                                <Text className="text-sm text-primary-700 font-bold">{Number(exp.amount).toFixed(2)} €</Text>
                                                <Text className='text-xs text-red-500'>{!exp.isSignedDirectDebitMandate && 'Falta la autorización de domiciliación bancaria'}</Text>
                                            </div>
                                        </div>
                                    </label>
                                ))}
                            </div>

                            <div className="flex justify-center items-center gap-10 mt-4">
                                {totalPages > 1 && (
                                    <div>
                                        {Array.from({ length: totalPages }, (_, i) => (
                                            <button
                                                key={i + 1}
                                                type="button"
                                                className={`px-3 py-1 rounded border ${currentPage === i + 1 ? 'bg-primary-500 text-white border-primary-500' : 'bg-white text-gray-700 border-gray-300'} transition`}
                                                onClick={() => setCurrentPage(i + 1)}
                                            >
                                                {i + 1}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <CustomSelect
                                    id="pageSize"
                                    label="Resultados por página"
                                    value={{ value: pageSize, label: `${pageSize} resultados` }}
                                    options={[10, 20, 50, 100].map(size => ({ value: size, label: `${size} resultados` }))}
                                    onChange={(e: any) => {
                                        setPageSize(e.value);
                                        setCurrentPage(1); // Reset to first page when changing page size
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    <div className={
                        cn('flex items-center my-3 md:col-span-2',
                            {
                                'justify-between': expirations?.expiration_ids?.length > 0,
                                'justify-end': !expirations,
                            }
                        )
                    }>
                        {expirations && expirations.expiration_ids?.length > 0 && remittanceData.corporationId && remittanceData.bankName && remittanceData.amount && (
                            <div className='flex flex-col' >
                                <span>Cantidad a remesar: {
                                    Number(expirations?.total_expirations_amount).toLocaleString('es-ES', {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 2,
                                        useGrouping: true,
                                    })
                                } €</span>
                                < span > Cantidad restante: {
                                    Number(expirations?.total_pending_amount).toLocaleString('es-ES', {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 2,
                                        useGrouping: true,
                                    })
                                } €</span>
                            </div>
                        )}

                        <Button type='submit' size='md' isLoading={isLoading} disabled={isLoading || formik.values.expirationIds.length === 0 || !remittanceData.dateRange.from || !remittanceData.dateRange.to || !remittanceData.corporationId || !remittanceData.bankName || !remittanceData.amount}>
                            Generar
                        </Button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default RemittanceModal;